/**
 * "A player uses the item they hold on a mob": name tag, piglin barter,
 * lighting a creeper, shearing, milking, breeding food, and the wolf / cat /
 * parrot right-click (taming, sitting). DOM-free: the world side (the name on
 * the mob, wool on the ground) happens here through `MobUseWorld`, and what it
 * costs the player comes back as a `MobUseFx` for whoever owns that player's
 * inventory to apply. One function, so the local right-click and a server
 * running it for a remote player follow the same rules.
 */

import { I } from '../game/itemIds';
import { applyNameTag } from '../game/itemUse/nameTag';
import { isEmpty, type Stack } from '../game/types';
import { FLINT_AND_STEEL_ID } from '../items';
import { Sheep } from './animals';
import { feedAnimal, isBreedFood } from './breeding';
import { Creeper } from './hostile';
import type { Mob } from './mob';
import { Piglin } from './nether/mobs';
import { isRightClickable } from './overworld2/interact';
import { mobs3Use, mobs3UseLikely } from './mobs3/use';

/** What a use on a mob does to the using player's inventory. */
export interface MobUseFx {
  /** Held items used up. */
  consume?: number;
  /** Durability points worn off the held tool. */
  wear?: number;
  /** An item handed back, as [id, count, dmg] (a milk bucket). */
  give?: [number, number, number];
}

/** The world as a mob use needs it. */
export interface MobUseWorld {
  /** Drop a stack into the world (sheared wool). */
  spawnItem(stack: Stack, x: number, y: number, z: number, vx: number, vy: number, vz: number): void;
  /** A creeper was lit at (x, y, z). */
  ignite?(x: number, y: number, z: number): void;
  rnd(): number;
}

/** Wolf / cat / parrot: their own right-click (works with an empty hand too). */
export function usePet(world: MobUseWorld, mob: Mob, held: Stack): MobUseFx | null {
  if (!isRightClickable(mob)) return null;
  const r = mob.interact(isEmpty(held) ? 0 : held.id, world.rnd);
  return r.handled ? { consume: r.consumed ? 1 : 0 } : null;
}

/** Could `held` do anything to `mob`? Lets a caller skip unrelated right-clicks
 *  (placing a block past a cow) without running the use. */
export function itemUseLikely(mob: Mob, held: Stack): boolean {
  if (isEmpty(held)) return false;
  return (held.id === I.NAME_TAG && !!held.name) || (mob instanceof Piglin && held.id === I.GOLD_INGOT)
    || (held.id === FLINT_AND_STEEL_ID && mob instanceof Creeper) || (held.id === I.SHEARS && mob instanceof Sheep)
    || (held.id === I.BUCKET && mob.type === 'cow') || mobs3UseLikely(mob, held) || isBreedFood(mob.type, held.id);
}

/** The held item used on `mob`; null when it does nothing there. */
export function useItemOnMob(world: MobUseWorld, mob: Mob, held: Stack): MobUseFx | null {
  if (isEmpty(held)) return null;
  // a renamed name tag names the mob and keeps it from despawning
  if (held.id === I.NAME_TAG && applyNameTag(mob, held)) return { consume: 1 };
  // a gold ingot offered to a calm piglin starts a barter
  if (mob instanceof Piglin && mob.tryBarter(held.id)) return { consume: 1 };
  if (held.id === FLINT_AND_STEEL_ID && mob instanceof Creeper && !mob.ignited) {
    mob.ignited = true;
    world.ignite?.(mob.x, mob.y, mob.z);
    return { wear: 1 };
  }
  if (held.id === I.SHEARS && mob instanceof Sheep) {
    const wool = mob.shear(world.rnd);
    if (wool.length === 0) return null;
    for (const s of wool) world.spawnItem(s, mob.x, mob.y + 1, mob.z, (world.rnd() - 0.5) * 0.2, 0.1 + world.rnd() * 0.05, (world.rnd() - 0.5) * 0.2);
    return { wear: 1 };
  }
  // milk the cow: the empty bucket becomes a milk bucket
  if (held.id === I.BUCKET && mob.type === 'cow' && !(mob as unknown as { baby?: boolean }).baby) return { consume: 1, give: [I.MILK_BUCKET, 1, 0] };
  const m3 = mobs3Use(world, mob, held);
  if (m3) return m3;
  return feedAnimal(mob, held.id) ? { consume: 1 } : null;
}

/** Any use of `held` on `mob`: the pet right-click first, then the item. */
export function useOnMob(world: MobUseWorld, mob: Mob, held: Stack): MobUseFx | null {
  return usePet(world, mob, held) ?? useItemOnMob(world, mob, held);
}
